import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NbLoginComponent } from '@nebular/auth';
import { NbAlertModule, NbButtonModule, NbCheckboxModule, NbInputModule } from '@nebular/theme';

/**
 * Nebular's stock login template closes with a "Don't have an account? Register" link. This CRM is
 * internal: there is no self-service sign-up, accounts are provisioned out of band (Firebase console
 * or tools/custom-claim-setter.js), so the app.routes.ts `auth/register` route is gone and this
 * subclass drops the link that advertised it. Behaviour is otherwise Nebular's, inherited unchanged.
 *
 * The social-sign-in block is dropped with it: the app registers a single Firebase password strategy
 * (app.config.ts) and both environments configure `forms.login.socialLinks: []`, so it never rendered.
 */
@Component({
  standalone: true,
  selector: 'clc-login',
  imports: [FormsModule, RouterLink, NbAlertModule, NbButtonModule, NbCheckboxModule, NbInputModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h1 id="title" class="title">Login</h1>
    <p class="sub-title">Hello! Log in with your email.</p>

    @if (showMessages.error && errors.length && !submitted) {
      <nb-alert outline="danger" role="alert">
        <p class="alert-title"><b>Oh snap!</b></p>
        <ul class="alert-message-list">
          @for (error of errors; track error) {
            <li class="alert-message">{{ error }}</li>
          }
        </ul>
      </nb-alert>
    }

    @if (showMessages.success && messages.length && !submitted) {
      <nb-alert outline="success" role="alert">
        <p class="alert-title"><b>Hooray!</b></p>
        <ul class="alert-message-list">
          @for (message of messages; track message) {
            <li class="alert-message">{{ message }}</li>
          }
        </ul>
      </nb-alert>
    }

    <form (ngSubmit)="login()" #form="ngForm" aria-labelledby="title">
      <div class="form-control-group">
        <label class="label" for="input-email">Email address:</label>
        <input
          nbInput
          fullWidth
          [(ngModel)]="user.email"
          #email="ngModel"
          name="email"
          id="input-email"
          pattern=".+@.+\\..+"
          placeholder="Email address"
          fieldSize="large"
          autofocus
          [status]="email.dirty ? (email.invalid ? 'danger' : 'success') : 'basic'"
          [required]="getConfigValue('forms.validation.email.required')"
          [attr.aria-invalid]="email.invalid && email.touched ? true : null"
        />
        @if (email.invalid && email.touched) {
          @if (email.errors?.['required']) {
            <p class="caption status-danger">Email is required!</p>
          }
          @if (email.errors?.['pattern']) {
            <p class="caption status-danger">Email should be the real one!</p>
          }
        }
      </div>

      <div class="form-control-group">
        <span class="label-with-link">
          <label class="label" for="input-password">Password:</label>
          <a class="forgot-password caption-2" routerLink="../request-password">Forgot Password?</a>
        </span>
        <input
          nbInput
          fullWidth
          [(ngModel)]="user.password"
          #password="ngModel"
          name="password"
          type="password"
          id="input-password"
          placeholder="Password"
          fieldSize="large"
          [status]="password.dirty ? (password.invalid ? 'danger' : 'success') : 'basic'"
          [required]="getConfigValue('forms.validation.password.required')"
          [minlength]="getConfigValue('forms.validation.password.minLength')"
          [maxlength]="getConfigValue('forms.validation.password.maxLength')"
          [attr.aria-invalid]="password.invalid && password.touched ? true : null"
        />
        @if (password.invalid && password.touched) {
          @if (password.errors?.['required']) {
            <p class="caption status-danger">Password is required!</p>
          }
          @if (password.errors?.['minlength'] || password.errors?.['maxlength']) {
            <p class="caption status-danger">
              Password should contain from {{ getConfigValue('forms.validation.password.minLength') }} to
              {{ getConfigValue('forms.validation.password.maxLength') }} characters
            </p>
          }
        }
      </div>

      <div class="form-control-group accept-group">
        @if (rememberMe) {
          <nb-checkbox name="rememberMe" [(ngModel)]="user.rememberMe">Remember me</nb-checkbox>
        }
      </div>

      <button
        nbButton
        fullWidth
        status="primary"
        size="large"
        [disabled]="submitted || !form.valid"
        [class.btn-pulse]="submitted"
      >
        Log In
      </button>
    </form>
  `,
})
export class LoginComponent extends NbLoginComponent {}
